/**
 * InboxSettingsTab — Settings › Inbox (Amendment 1).
 *
 * Admin-only management of departmental unified inboxes: the list, the editor, the
 * RBAC scope script. It sits beside Integrations
 * because that is where an admin already goes to connect Microsoft — but it is a
 * separate tab, since the thing being configured is who may read whose mail rather
 * than which account is linked.
 */
import React, { useState } from 'react';
import {
    AlertTriangle,
    CheckCircle2,
    Loader2,
    Mailbox,
    Pencil,
    Plus,
    Trash2,
    Users,
} from 'lucide-react';
import { toast } from 'sonner';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../../ui/alert-dialog';
import { Button } from '../../../ui/button';
import InboxEditorModal from './InboxEditorModal';
import { isMemberOnly } from '../../../inbox/inboxRules';
import {
    useCreateInbox,
    useDeleteInbox,
    useInboxEmployees,
    useInboxes,
    useOrphanedInboxes,
    useRecoverOrphanedInbox,
    useInboxMailboxes,
    useMailboxAccessRequests,
    useMailboxTokens,
    useRequestMailboxAccess,
    useResendMailboxAccess,
    useRevokeMailboxAccess,
    useCancelMailboxAccess,
    useUpdateInbox,
} from '../../../../hooks/useUnifiedInbox';
import { providerForMailbox } from '../../../../utils/mailboxProvider';
import { connectionsFromRefs } from '../../../../utils/inboxMailboxes';

function InboxRow({ inbox, onEdit, onDelete }) {
    const memberOnly = isMemberOnly(inbox);
    const grants = (inbox.access_grants || []).length;
    const members = (inbox.member_graph_ids || []).length + (inbox.member_smtps || []).length;

    return (
        <div
            className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-stone-100 last:border-b-0"
            data-testid={`inbox-settings-row-${inbox.inbox_id}`}
        >
            <span className="flex-1 min-w-[180px]">
                <span className="block text-sm font-medium text-stone-900">{inbox.name}</span>
                <span className="block text-xs text-stone-400">
                    {memberOnly ? 'members only' : inbox.distro_smtp}
                </span>
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-stone-500 w-28">
                <Users className="w-3.5 h-3.5" />
                {members} mailbox{members === 1 ? '' : 'es'}
            </span>
            <span className="text-xs text-stone-500 w-24">
                {grants} grant{grants === 1 ? '' : 's'}
            </span>
            <span className="w-32">
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    scoped
                </span>
            </span>
            <span className="flex items-center gap-1">
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => onEdit(inbox)}
                    data-testid={`inbox-edit-${inbox.inbox_id}`}
                >
                    <Pencil className="w-3.5 h-3.5 mr-1" />
                    Edit
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-red-500 hover:text-red-600"
                    onClick={() => onDelete(inbox)}
                    data-testid={`inbox-delete-${inbox.inbox_id}`}
                >
                    <Trash2 className="w-3.5 h-3.5" />
                </Button>
            </span>
        </div>
    );
}

function OrphanInboxRow({ inbox, onRecover, isRecovering }) {
    return (
        <div
            className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-amber-100 last:border-b-0"
            data-testid={`inbox-orphan-row-${inbox.inbox_id}`}
        >
            <span className="flex-1 min-w-[180px]">
                <span className="block text-sm font-medium text-stone-900">{inbox.name}</span>
                <span className="block text-xs text-amber-700">
                    No active manager grant · recovery required
                </span>
            </span>
            <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
                onClick={() => onRecover(inbox)}
                disabled={isRecovering}
                data-testid={`inbox-recover-${inbox.inbox_id}`}
            >
                {isRecovering ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                Recover
            </Button>
        </div>
    );
}


export default function InboxSettingsTab() {
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);

    const { data: inboxes = [], isLoading, error } = useInboxes();
    const {
        data: orphanState = { data: [], total: 0, org_inbox_count: 0 },
        isLoading: orphansLoading,
        error: orphansError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useOrphanedInboxes();
    const recoverOrphan = useRecoverOrphanedInbox();
    const orphanedInboxes = orphanState.data || [];
    // The employee list is only fetched while the editor is open — it is a full
    // user list and there is no reason to load it on every page visit.
    const { data: employees = [] } = useInboxEmployees(editorOpen);
    const { data: mailboxTokens = [] } = useMailboxTokens();
    const { data: msAccessRequests = [] } = useMailboxAccessRequests('microsoft');
    const { data: googleAccessRequests = [] } = useMailboxAccessRequests('google');

    // Mailboxes already attached to the inbox being edited. This is the ONLY
    // surface that reports a Google delegated grant: it lands in
    // `mailbox_connections`, so `useMailboxTokens` above — which reads the
    // Microsoft-only `mailbox_tokens` — cannot see it, and the Members tab
    // showed an approved Gmail mailbox as "Not Requested" without it. Scoped to
    // the inbox on purpose: "does this inbox have access to this mailbox" is
    // exactly the question the Members tab asks.
    const { data: inboxMailboxesData } = useInboxMailboxes(editing?.inbox_id);
    const mailboxConnections = connectionsFromRefs(inboxMailboxesData);
    const accessRequests = [...msAccessRequests, ...googleAccessRequests];
    const requestMailboxAccess = useRequestMailboxAccess();
    const revokeMailboxAccess = useRevokeMailboxAccess();
    const resendMailboxAccess = useResendMailboxAccess();
    const cancelMailboxAccess = useCancelMailboxAccess();

    const createInbox = useCreateInbox();
    const updateInbox = useUpdateInbox();
    const deleteInbox = useDeleteInbox();

    const featureOff = error?.response?.status === 404;

    if (featureOff) {
        return (
            <div className="max-w-2xl" data-testid="inbox-settings-disabled">
                <div className="flex items-start gap-2 p-4 rounded-lg bg-stone-50 border border-stone-200">
                    <AlertTriangle className="w-4 h-4 text-stone-400 mt-0.5" />
                    <p className="text-sm text-stone-600">
                        Departmental unified inboxes are not enabled for this workspace. Ask your
                        NurtureCRM administrator to switch on the unified inbox for this tenant.
                    </p>
                </div>
            </div>
        );
    }

    const save = async draft => {
        if (editing) {
            await updateInbox.mutateAsync({ inboxId: editing.inbox_id, ...draft });
        } else {
            await createInbox.mutateAsync(draft);
        }
    };

    const recover = async inbox => {
        if (!window.confirm(`Recover ${inbox.name} and grant yourself manager access?`)) return;
        try {
            await recoverOrphan.mutateAsync(inbox.inbox_id);
            toast.success(`${inbox.name} recovered — you now own this inbox`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not recover the inbox');
        }
    };

    const doDelete = async () => {
        const target = pendingDelete;
        setPendingDelete(null);
        if (!target) return;
        try {
            await deleteInbox.mutateAsync(target.inbox_id);
            toast.success(`${target.name} deleted`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not delete the inbox');
        }
    };

    return (
        <div className="space-y-5" data-testid="inbox-settings-tab">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3
                        className="text-sm font-semibold text-stone-900 flex items-center gap-2"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                        <Mailbox className="w-4 h-4 text-stone-500" />
                        Departmental inboxes
                    </h3>
                    <p className="text-xs text-stone-500 mt-1 max-w-xl">
                        Each inbox is one shared address plus any individual mailboxes you pool
                        into it. Grant users access per inbox — everyone sees only the inboxes
                        they are granted.
                    </p>
                </div>
                <Button
                    size="sm"
                    className="bg-stone-900 hover:bg-stone-800"
                    onClick={() => {
                        setEditing(null);
                        setEditorOpen(true);
                    }}
                    data-testid="inbox-settings-new"
                >
                    <Plus className="w-4 h-4 mr-1" />
                    New inbox
                </Button>
            </div>

            <div className="border border-stone-200 rounded-lg overflow-hidden">
                {isLoading ? (
                    <div className="flex items-center gap-2 px-4 py-6 text-sm text-stone-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading inboxes…
                    </div>
                ) : inboxes.length === 0 ? (
                    <p
                        className="px-4 py-6 text-sm text-stone-400"
                        data-testid="inbox-settings-empty"
                    >
                        {orphansLoading
                            ? 'Checking inbox ownership…'
                            : orphansError
                              ? 'Could not check inbox ownership. Refresh and try again.'
                              : orphanState.org_inbox_count === 0
                                ? 'No departmental inboxes yet. Create one to pool a shared address and your team\'s mailboxes into a single view.'
                                : 'You hold no grants on any inbox in this organisation.'}
                    </p>
                ) : (
                    inboxes.map(inbox => (
                        <InboxRow
                            key={inbox.inbox_id}
                            inbox={inbox}
                            onEdit={target => {
                                setEditing(target);
                                setEditorOpen(true);
                            }}
                            onDelete={setPendingDelete}
                        />
                    ))
                )}
            </div>

            {orphansError && inboxes.length > 0 && (
                <div
                    className="px-4 py-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg"
                    data-testid="inbox-orphans-error"
                >
                    Could not check for recoverable inboxes. Refresh and try again.
                </div>
            )}

            {!orphansLoading && !orphansError && orphanedInboxes.length > 0 && (
                <div
                    className="border border-amber-200 rounded-lg overflow-hidden bg-amber-50/40"
                    data-testid="inbox-orphans-section"
                >
                    <div className="flex items-start gap-2 px-4 py-3 border-b border-amber-200">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                        <div>
                            <h4 className="text-sm font-semibold text-amber-900">Recoverable inboxes</h4>
                            <p className="text-xs text-amber-800 mt-0.5">
                                These inboxes have no active manager. Recovery grants you manager access;
                                it does not expose messages until the grant is written.
                            </p>
                        </div>
                    </div>
                    {orphanedInboxes.map(inbox => (
                        <OrphanInboxRow
                            key={inbox.inbox_id}
                            inbox={inbox}
                            onRecover={recover}
                            isRecovering={recoverOrphan.isPending}
                        />
                    ))}
                    {hasNextPage && (
                        <div className="px-4 py-3 border-t border-amber-200">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs text-amber-900"
                                onClick={() => fetchNextPage?.()}
                                disabled={isFetchingNextPage}
                                data-testid="inbox-orphans-load-more"
                            >
                                {isFetchingNextPage ? 'Loading more…' : 'Load more recoverable inboxes'}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            <InboxEditorModal
                open={editorOpen}
                onOpenChange={setEditorOpen}
                inbox={editing}
                employees={employees}
                onSave={save}
                isSaving={createInbox.isPending || updateInbox.isPending}
                mailboxTokens={mailboxTokens}
                accessRequests={accessRequests}
                mailboxConnections={mailboxConnections}
                onRequestAccess={async (mail, provider) => {
                    try {
                        await requestMailboxAccess.mutateAsync({
                            mailboxEmail: mail,
                            inboxId: editing?.inbox_id || '',
                            // Omitted for a brand-new mailbox: the control plane
                            // resolves the platform from MX. Defaulting to
                            // 'microsoft' here is what misrouted Workspace
                            // custom domains.
                            ...(provider ? { provider } : {}),
                        });
                        toast.success(`Access request sent to ${mail}`);
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not send access request');
                    }
                }}
                onRevoke={async (mail) => {
                    try {
                        const res = await revokeMailboxAccess.mutateAsync(mail);
                        // Say what happened to the MAIL, not just the access.
                        //
                        // Revoking used to leave every message already ingested
                        // from the mailbox visible to the whole inbox, and the
                        // toast said "Access revoked" either way — so a partial
                        // revoke was indistinguishable from a complete one
                        // (issue #486). The count is the signal that the data
                        // went too.
                        const removed = res?.data?.removed_messages ?? 0;
                        // "deleted", not "removed". The revoke destroys the
                        // rows rather than untagging them, matching what it
                        // does to the credential, and the admin should know
                        // that before they reach for an undo that does not
                        // exist.
                        //
                        // `identity_resolved: false` means the mailbox could
                        // not be resolved to a delivery identity, so NOTHING
                        // was deleted — reporting "0 deleted" there would read
                        // as "there was nothing to delete", which is the same
                        // silent half-revoke this fix exists to end.
                        const resolved = res?.data?.identity_resolved !== false;
                        if (!resolved) {
                            toast.warning
                                ? toast.warning(
                                      `Access revoked for ${mail}, but its mail could not be identified — no messages were deleted`
                                  )
                                : toast.success(
                                      `Access revoked for ${mail} — its mail could not be identified and was not deleted`
                                  );
                        } else {
                            toast.success(
                                removed > 0
                                    ? `Access revoked for ${mail} — ${removed} message${
                                          removed === 1 ? '' : 's'
                                      } permanently deleted from the shared inbox`
                                    : `Access revoked for ${mail} — no messages to delete`
                            );
                        }
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not revoke access');
                    }
                }}
                onResend={async (mail) => {
                    try {
                        await resendMailboxAccess.mutateAsync({
                            mail,
                            provider: providerForMailbox(mail, { mailboxTokens, accessRequests }),
                        });
                        toast.success(`Access request resent to ${mail}`);
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not resend access request');
                    }
                }}
                onCancel={async (mail) => {
                    try {
                        await cancelMailboxAccess.mutateAsync({
                            mail,
                            provider: providerForMailbox(mail, { mailboxTokens, accessRequests }),
                        });
                        toast.success(`Access request cancelled for ${mail}`);
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not cancel');
                    }
                }}
                isRequesting={
                    requestMailboxAccess.isPending ||
                    revokeMailboxAccess.isPending ||
                    resendMailboxAccess.isPending ||
                    cancelMailboxAccess.isPending
                }
                isCancelling={cancelMailboxAccess.isPending}
                onAutoSave={async (updatedDraft) => {
                    try {
                        if (editing) {
                            await updateInbox.mutateAsync({ inboxId: editing.inbox_id, ...updatedDraft });
                            toast.success('Inbox updated');
                        } else {
                            // For new inboxes, create on first email add
                            const result = await createInbox.mutateAsync(updatedDraft);
                            // Switch to edit mode with the new inbox
                            const newInbox = result?.data || result;
                            if (newInbox?.inbox_id) {
                                setEditing(newInbox);
                            }
                            toast.success('Inbox created');
                        }
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not save the inbox');
                    }
                }}
            />

            <AlertDialog
                open={Boolean(pendingDelete)}
                onOpenChange={open => !open && setPendingDelete(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {pendingDelete?.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {/* Say what is NOT deleted. An admin reorganising departments
                                should not have to guess whether this destroys mail history. */}
                            Messages already ingested stay in your communications history — only
                            the routing and the access grants stop. Graph subscriptions for
                            mailboxes no other inbox uses are torn down.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={doDelete}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="inbox-delete-confirm"
                        >
                            Delete inbox
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
